import type { YAxisOptions, YAxisPlotLinesOptions } from 'highcharts';

import Highcharts from '@/components/chart/highcharts';

import { defaultYAxisOptions } from '../chart/defaultHighChartOptions.ts';

const primaryYAxisId = 'y';

type YAxisLayout = {
  plotTypeToYAxisIndex: Record<string, number>;
  uniqueUnitsInOrder: (string | undefined)[];
};

/** First-seen unit order matches plot type selection order. */
const buildYAxisLayout = (
  plotTypes: string[],
  units: Record<string, string | undefined>
): YAxisLayout => {
  const unitToIndex = new Map<string | undefined, number>();
  const plotTypeToYAxisIndex: Record<string, number> = {};
  const uniqueUnitsInOrder: (string | undefined)[] = [];

  for (const plotType of plotTypes) {
    const unit = units[plotType];
    let index = unitToIndex.get(unit);
    if (index === undefined) {
      index = uniqueUnitsInOrder.length;
      unitToIndex.set(unit, index);
      uniqueUnitsInOrder.push(unit);
    }
    plotTypeToYAxisIndex[plotType] = index;
  }

  return { plotTypeToYAxisIndex, uniqueUnitsInOrder };
};

/** Maps each plot type (event class) to a y-axis index by shared unit. */
export const getYAxisIndexByPlotType = (
  plotTypes: string[],
  units: Record<string, string | undefined>
): Record<string, number> => buildYAxisLayout(plotTypes, units).plotTypeToYAxisIndex;

export type SyncChartYAxesOptions = {
  primaryAxisOverrides?: YAxisOptions;
  plotLines?: YAxisPlotLinesOptions[];
};

/**
 * One y-axis per distinct unit (primary left; further units on the opposite side).
 * Replaces hiding the axis title whenever multiple plot types were selected.
 */
export const syncChartYAxes = (
  chart: Highcharts.Chart | undefined,
  plotTypes: string[],
  units: Record<string, string | undefined>,
  options?: SyncChartYAxesOptions
): Record<string, number> => {
  const { plotTypeToYAxisIndex, uniqueUnitsInOrder } = buildYAxisLayout(plotTypes, units);

  const axesOptions: YAxisOptions[] = uniqueUnitsInOrder.map((unit, index) => ({
    ...defaultYAxisOptions,
    ...(index === 0 ? options?.primaryAxisOverrides : undefined),
    id: index === 0 ? primaryYAxisId : `${primaryYAxisId}-${index}`,
    opposite: index > 0,
    title: {
      ...defaultYAxisOptions.title,
      text: unit,
    },
    gridLineWidth: index === 0 ? defaultYAxisOptions.gridLineWidth : 0,
    plotLines: index === 0 ? options?.plotLines : undefined,
  }));

  if (!chart) {
    return plotTypeToYAxisIndex;
  }

  while (chart.yAxis.length > axesOptions.length) {
    chart.yAxis[chart.yAxis.length - 1]?.remove(false);
  }

  axesOptions.forEach((axisOptions, index) => {
    if (chart.yAxis[index]) {
      chart.yAxis[index].update(axisOptions, false);
    } else {
      chart.addAxis(axisOptions, false, false, false);
    }
  });

  return plotTypeToYAxisIndex;
};

/** Replaces plot lines on an axis. Prefer this over `addPlotLine`/`removePlotLine`. */
export const setAxisPlotLines = (
  axis: Highcharts.Axis | undefined,
  plotLines: YAxisPlotLinesOptions[]
) => {
  axis?.update({ plotLines });
};
